import { FormikInput } from "@faire/retailer-visitor-shared/components/Form/FormikInput";
import { FormikPassword } from "@faire/retailer-visitor-shared/components/Form/FormikPassword";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import React, { useCallback } from "react";
import * as Yup from "yup";

type IFormikInputProps = React.ComponentProps<typeof FormikInput>;
type ITrackChangeProps = Pick<
  IFormikInputProps,
  "trackChange" | "blockErrorMsg"
>;

export const PasswordField: React.FC<{
  trackChange?: (name: string, value: string) => void;
}> = ({ trackChange }) => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <FormikPassword
      name="password"
      autoComplete="password"
      label={strictLocalize({
        defaultMessage: "Password",
      })}
      helper={strictLocalize({
        defaultMessage: "8 characters minimum",
        description: {
          text: "Password helper text",
          img: "https://cdn.faire.com/fastly/b4391deaf45afde6bb19c5a92254df2fd47d416b548324d209ec947087b79da3.png",
        },
      })}
      data-test-id="password"
      trackChange={(name) => trackChange?.(name, "")}
      shouldShowEyeButton
    />
  );
};

export const usePasswordSchema = () => {
  const { strictLocalize } = useStrictLocalization();
  const password = useCallback(
    (schema = Yup.string()) =>
      schema
        .min(
          8,
          strictLocalize({
            defaultMessage: "Must be at least 8 characters",
            description: {
              text: "Password input field helper text",
              img: "https://cdn.faire.com/fastly/916e5daceadec54811f555c3c90a468f3a83d32ff9b7bc79c90c625a0ab75da0.png",
            },
          })
        )
        .required(
          strictLocalize({
            defaultMessage: "Please enter a password",
          })
        ),
    [strictLocalize]
  );
  return { password };
};

export const FirstNameField: React.FC<
  ITrackChangeProps & {
    ref?: React.Ref<HTMLInputElement>;
  }
> = ({ trackChange, ref }) => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <FormikInput
      name="first_name"
      autoComplete="given-name"
      label={strictLocalize({
        defaultMessage: "First name",
      })}
      data-test-id="first-name"
      trackChange={trackChange}
      ref={ref}
    />
  );
};

export const useFirstNameSchema = () => {
  const { strictLocalize } = useStrictLocalization();
  const first_name = useCallback(
    (schema = Yup.string()) =>
      schema
        .trim()
        .max(
          255,
          strictLocalize({
            defaultMessage: "Cannot be longer than 255 characters",
          })
        )
        .required(
          strictLocalize({
            defaultMessage: "Please enter your first name",
          })
        ),
    [strictLocalize]
  );
  return { first_name };
};

export const LastNameField: React.FC<ITrackChangeProps> = ({ trackChange }) => {
  const { strictLocalize } = useStrictLocalization();

  return (
    <FormikInput
      name="last_name"
      autoComplete="family-name"
      label={strictLocalize({
        defaultMessage: "Last name",
      })}
      trackChange={trackChange}
      data-test-id="last-name"
    />
  );
};

export const useLastNameSchema = () => {
  const { strictLocalize } = useStrictLocalization();
  const last_name = useCallback(
    (schema = Yup.string()) =>
      schema
        .trim()
        .max(
          255,
          strictLocalize({
            defaultMessage: "Cannot be longer than 255 characters",
          })
        )
        .required(
          strictLocalize({
            defaultMessage: "Please enter your last name",
          })
        ),
    [strictLocalize]
  );
  return { last_name };
};

export const EmailAddressField: React.FC<
  ITrackChangeProps & { disabled?: boolean }
> = ({ trackChange, blockErrorMsg, disabled }) => {
  const { strictLocalize } = useStrictLocalization();

  return (
    <FormikInput
      name="email_address"
      autoComplete="email"
      type="email"
      label={strictLocalize({
        defaultMessage: "Business email address",
      })}
      trackChange={trackChange}
      data-test-id="business-email-address"
      blockErrorMsg={blockErrorMsg}
      disabled={disabled}
    />
  );
};

export const useEmailAddressSchema = () => {
  const { strictLocalize } = useStrictLocalization();
  const email_address = useCallback(
    (schema = Yup.string()) =>
      schema
        .trim()
        .email(
          strictLocalize({
            defaultMessage: "Not a valid email",
          })
        )
        .required(
          strictLocalize({
            defaultMessage: "Please enter an email",
          })
        ),
    [strictLocalize]
  );
  return { email_address };
};
